"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, useEffect } from "react";
import { AdSlot } from "./AdSlot";
import { measureNavigationHeight } from "@/components/layout/headers/NavHeightTracker";
const CONTENT_HALF = "43.75rem"; // half of max-w-350 (87.5rem)
const GAP = 20;
interface StackedWallpaperRailsProps {
  hasLeftTop: boolean;
  hasLeftBottom: boolean;
  hasRightTop: boolean;
  hasRightBottom: boolean;
}

/**
 * Stacked wallpaper rails: two ads per side (static top + sticky bottom).
 *
 * All four ads use `position: fixed`. A scroll listener drives their `top`:
 * - Top ads scroll with the page (move up as user scrolls).
 * - Bottom ads pin below the nav once the top ad scrolls out of view,
 *   and stop before the footer.
 */
export function StackedWallpaperRails(t0) {
  const $ = _c(11);
  const {
    hasLeftTop,
    hasLeftBottom,
    hasRightTop,
    hasRightBottom
  } = t0;
  const leftTopRef = useRef(null);
  const leftBottomRef = useRef(null);
  const rightTopRef = useRef(null);
  const rightBottomRef = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const navEl = document.getElementById("site-navigation");
      const update = () => {
        const navH = navEl && measureNavigationHeight(navEl) || 120;
        const scrollY = window.scrollY;
        const footer = document.querySelector("footer");
        const footerTop = footer ? footer.getBoundingClientRect().top : window.innerHeight;
        const sides = [[leftTopRef.current, leftBottomRef.current], [rightTopRef.current, rightBottomRef.current]];
        for (const [topEl, bottomEl] of sides) {
          const topH = topEl?.offsetHeight || 0;
          const bottomH = bottomEl?.offsetHeight || 0;
          if (topEl) {
            const topAdTop = navH + GAP - scrollY;
            topEl.style.top = `${topAdTop}px`;
            topEl.style.visibility = topAdTop + topH > 0 ? "visible" : "hidden";
          }
          if (bottomEl) {
            const naturalTop = navH + GAP + topH + GAP - scrollY;
            const pinnedTop = navH + GAP;
            const footerLimit = footerTop - bottomH - GAP;
            const top = Math.min(Math.max(naturalTop, pinnedTop), footerLimit);
            bottomEl.style.top = `${top}px`;
          }
        }
      };
      window.addEventListener("scroll", update, {
        passive: true
      });
      window.addEventListener("resize", update, {
        passive: true
      });
      update();
      const ro = navEl ? new ResizeObserver(update) : null;
      if (ro && navEl) {
        ro.observe(navEl);
      }
      return () => {
        window.removeEventListener("scroll", update);
        window.removeEventListener("resize", update);
        ro?.disconnect();
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const railStyle = _temp;
  let t3;
  if ($[2] !== hasLeftBottom || $[3] !== hasLeftTop) {
    t3 = (hasLeftTop || hasLeftBottom) && <>{hasLeftTop && <div ref={leftTopRef} className="wallpaper-rail" style={railStyle("left")}><AdSlot id="wallpaper-left-top" /></div>}{hasLeftBottom && <div ref={leftBottomRef} className="wallpaper-rail" style={railStyle("left")}><AdSlot id="wallpaper-left-bottom" /></div>}</>;
    $[2] = hasLeftBottom;
    $[3] = hasLeftTop;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== hasRightBottom || $[6] !== hasRightTop) {
    t4 = (hasRightTop || hasRightBottom) && <>{hasRightTop && <div ref={rightTopRef} className="wallpaper-rail" style={railStyle("right")}><AdSlot id="wallpaper-right-top" /></div>}{hasRightBottom && <div ref={rightBottomRef} className="wallpaper-rail" style={railStyle("right")}><AdSlot id="wallpaper-right-bottom" /></div>}</>;
    $[5] = hasRightBottom;
    $[6] = hasRightTop;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function _temp(side) {
  return {
    position: "fixed",
    top: 0,
    zIndex: 20,
    pointerEvents: "auto",
    ...(side === "left" ? {
      right: `calc(50% + ${CONTENT_HALF})`
    } : {
      left: `calc(50% + ${CONTENT_HALF})`
    })
  };
}